import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { Screens } from '~/constants/config/permissions';
import { ProductPageSkeleton } from '~/features/content/components/product-page/product-page-skeleton';
import { QcFullPage } from '~/features/content/components/qc/qc-full-page';
import { PRODUCT_PAGE_FROM, PRODUCT_PAGE_TABS } from '~/features/content/utils/product-page';
import { requirePermission } from '~/utils/guards';

const ProductPage = lazy(() => import('~/features/content/pages/product-page'));

// Where it was opened from and the tab live in the URL, so a shared link,
// a reload or the back button lands on the same view.
const searchSchema = z.object({
  /** Which list opened the page — drives the breadcrumb and the back link. */
  from: z.enum(PRODUCT_PAGE_FROM).optional().catch(undefined),
  tab: z.enum(PRODUCT_PAGE_TABS).optional().catch(undefined),
  /** Channel, for a run scored on several (the score read needs one then). */
  platform: z.string().optional().catch(undefined),
  /** Id of the list the page was opened from (saved in the browser) — drives Previous / Next. */
  nav: z.string().max(32).optional().catch(undefined),
});

/** One product of one run at `/content/runs/$runId/products/$productId` — a full page shared by QC checks, catalog runs and jobs. */
export const Route = createFileRoute(
  '/_authenticated/_tenant/content_/runs/$runId/products/$productId',
)({
  // Outside the Content layout (its own full page), so it repeats the section's screen guard.
  beforeLoad: requirePermission(Screens.CONTENT_GENERATION),
  validateSearch: searchSchema,
  component: ProductRoute,
});

function ProductRoute() {
  const { runId, productId } = Route.useParams();
  const { from, tab, platform, nav } = Route.useSearch();
  return (
    <QcFullPage>
      {/* The product page's own frame while its code loads — not the run page's header and tabs. */}
      <Suspense fallback={<ProductPageSkeleton />}>
        <ProductPage
          runId={runId}
          productId={productId}
          from={from}
          tab={tab}
          platform={platform}
          nav={nav}
        />
      </Suspense>
    </QcFullPage>
  );
}
